import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Check, ChevronLeft, ChevronRight, Pencil } from "lucide-react";
import { BrandButton } from "@/components/PageHeader";
import { SetupSection } from "./BasicsSection";
import { api, errMsg } from "@/lib/api";

export const PRESETS = ["/images/event-congress.jpg", "/images/event-symposium.jpg", "/images/event-workshop.jpg"];

const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  try {
    return (await api.post("/uploads/banner", fd)).data.url;
  } catch (e) {
    toast.error(`${file.name}: ${errMsg(e)}`);
    return null;
  }
};

const Tool = ({ onClick, label, testId, children, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label} data-testid={testId}
    className="w-8 h-8 grid place-items-center rounded-md text-gray-600 hover:bg-[#FFF0F0] hover:text-[#E60000] disabled:opacity-30 disabled:hover:bg-transparent transition-colors">{children}</button>
);

const Tile = ({ src, primary, onSelect, index, tools }) => (
  <div className={`rounded-md overflow-hidden border-2 bg-white ${primary ? "border-[#E60000]" : "border-[#E5E7EB] hover:border-gray-300"}`} data-testid={`setup-banner-tile-${index}`}>
    <div className="relative aspect-[16/9]">
      <button type="button" onClick={onSelect} className="w-full h-full" data-testid={`setup-banner-select-${index}`}><img src={src} alt="" className="w-full h-full object-cover" /></button>
      {primary && <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#E60000] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm"><Check className="w-3 h-3" />Main banner</span>}
    </div>
    {tools && <div className="flex items-center justify-between px-1.5 py-1 border-t border-[#E5E7EB]">{tools}</div>}
  </div>
);

const UploadTools = ({ i, count, busy, move, edit, remove }) => (
  <>
    <div className="flex">
      <Tool onClick={() => move(i, -1)} disabled={busy || i === 0} label="Move left" testId={`setup-banner-left-${i}`}><ChevronLeft className="w-4 h-4" /></Tool>
      <Tool onClick={() => move(i, 1)} disabled={busy || i === count - 1} label="Move right" testId={`setup-banner-right-${i}`}><ChevronRight className="w-4 h-4" /></Tool>
    </div>
    <div className="flex">
      <Tool onClick={() => edit(i)} disabled={busy} label="Replace image" testId={`setup-banner-edit-${i}`}><Pencil className="w-4 h-4" /></Tool>
      <Tool onClick={() => remove(i)} disabled={busy || count === 1} label="Remove" testId={`setup-banner-remove-${i}`}><X className="w-4 h-4" /></Tool>
    </div>
  </>
);

export const BannersSection = ({ image, banners, set }) => {
  const ref = useRef(null);
  const editRef = useRef(null);
  const editing = useRef(-1);
  const [busy, setBusy] = useState(false);
  const uploads = banners;
  const gallery = uploads;
  const library = PRESETS.filter((p) => !banners.includes(p));

  const upload = async (files) => {
    setBusy(true);
    const added = (await Promise.all(files.map(uploadFile))).filter(Boolean);
    if (added.length) {
      set({ banners: [...added, ...uploads], image: added[0] });
      toast.success(`${added.length} banner${added.length > 1 ? "s" : ""} uploaded`);
    }
    ref.current.value = "";
    setBusy(false);
  };
  const move = (i, d) => {
    const next = [...uploads];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    set({ banners: next });
  };
  const remove = (i) => {
    const next = uploads.filter((_, j) => j !== i);
    set({ banners: next, ...(uploads[i] === image ? { image: next[0] } : {}) });
  };
  const edit = (i) => { editing.current = i; editRef.current.click(); };
  const replace = async (file) => {
    const i = editing.current;
    setBusy(true);
    const url = await uploadFile(file);
    if (url) {
      set({ banners: uploads.map((b, j) => (j === i ? url : b)), ...(uploads[i] === image ? { image: url } : {}) });
      toast.success("Banner replaced");
    }
    editRef.current.value = "";
    setBusy(false);
  };

  return (
    <SetupSection id="banners" step="04" title="Banner images" description="Upload banners or add them from the library. Tap a banner to make it the main one. Use the tools under each banner to reorder, replace or remove it. The main banner leads the homepage slide; the others follow in the order shown.">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <BrandButton type="button" onClick={() => ref.current.click()} disabled={busy} data-testid="setup-banner-upload-button"><Upload className="w-4 h-4" />{busy ? "Uploading…" : "Upload banners"}</BrandButton>
        <span className="text-xs text-gray-500">JPG, PNG or WebP · up to 5 MB each · wide images (16:9) look best</span>
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => e.target.files.length && upload([...e.target.files])} data-testid="setup-banner-file-input" />
        <input ref={editRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files.length && replace(e.target.files[0])} data-testid="setup-banner-replace-input" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="setup-banner-gallery">
        {gallery.map((src, i) => (
          <Tile key={src} src={src} index={i} primary={src === image} onSelect={() => set({ image: src })}
            tools={i < uploads.length && <UploadTools i={i} count={uploads.length} busy={busy} move={move} edit={edit} remove={remove} />} />
        ))}
      </div>
      {library.length > 0 && (
        <div className="mt-5" data-testid="setup-banner-library">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Library · tap to add</div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {library.map((src, i) => (
              <button key={src} type="button" onClick={() => set({ banners: [...banners, src] })} className="relative aspect-[16/9] rounded-md overflow-hidden border border-[#E5E7EB] hover:border-[#E60000] transition-colors" data-testid={`setup-banner-library-add-${i}`}>
                <img src={src} alt="" className="w-full h-full object-cover opacity-80" />
                <span className="absolute inset-0 grid place-items-center text-white text-xs font-semibold bg-black/30">+ Add</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </SetupSection>
  );
};
